import * as React from 'react'
import produce from 'immer'
import { Form, Spin, Button, Dropdown, Menu, Input, Tabs, message } from 'antd'
import { isNil, get, set, isEmpty, debounce, cloneDeep, unset, isEqual, camelCase, invoke, flow } from 'lodash'
import { withRouter } from 'react-router-dom'
import { showError, showClientNotifications, resourceNotFound, ValidationError } from 'helpers/errors'
import { getUserPermission, allowPricing } from 'helpers/auth'
import {
  createLabelFactory,
  createFieldFactory,
  createSaveDocumentItems,
  createSaveToleranceItems,
  createSaveWarrantyItems,
  createSaveImage,
  getChangedItems,
  createHandleSubmit,
  validateBarcode,
  isReadOnly,
  confirmUnsavedChanges,
} from 'helpers/formViews'
import { HARD_SPACE, tryParseFloat, DEBOUNCE, encodeState } from 'helpers/utils'
import { setSessionItem } from 'helpers/sessionStorage'
import { setStorageItem } from 'helpers/localStorage'
import serviceStatusOptions from 'options/assets/serviceStatusOptions'
import taskTypes from 'options/tasks/taskTypes'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import { createSaveFormInstances, createRefreshFormInstances, FormInstancesContext } from 'helpers/formInstances'
import Documents from 'containers/Documents/ChildListView'
import Lists from 'containers/Lists/ChildListView'
import Warranties from 'containers/Assets/Warranties'
import Tolerances from 'containers/Assets/Tolerances'
import History from 'containers/Assets/History'
import Tasks from 'containers/Assets/Tasks'
import FileUpload from 'elements/FileUpload'
import InputImage from 'elements/InputImage'
import FormButtons from 'elements/FormButtons'
import CopySchedule from 'containers/Assets/CopySchedule'
import FormInstances from 'containers/Assets/Forms'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_INSTANCES_REFRESH, FORM_CHANGE_EVENT } from 'options/events'
import { TRACK_ASSETS_AFTER_CLOSE_KEY } from 'components/TrackAssets'
import SelectAsset from 'containers/Assets/Select'
import Checkbox from 'elements/Checkbox'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import Divider from 'elements/Divider'
import Modal from 'elements/Modal'
import Drawer from 'elements/Drawer'
import Icon from 'elements/Icon'
import { Row, Col } from 'elements/Grid'
import ProcessSetupSheet from 'containers/ProcessSetupSheet/FormView'

const JobsFormView = linkTargets.jobRecord.formComponent
const InventoryFormView = linkTargets.inventoryRecord.formComponent

class FormView extends React.Component {
  state = {
    inventoryAvailability: {},
    listItems: [],
    warrantyItems: [],
    toleranceItems: [],
  }

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.fetchInventoryAvailability = debounce(this.fetchInventoryAvailability, DEBOUNCE)
    this.saveDocumentItems = createSaveDocumentItems(this, 'Asset')
    this.saveToleranceItems = createSaveToleranceItems(this)
    this.saveWarrantyItems = createSaveWarrantyItems(this)
    this.saveImage = createSaveImage(this, 'Asset')
    this.saveFormInstances = createSaveFormInstances(this, { objectType: 'Asset' })
    this.refreshFormInstances = createRefreshFormInstances(this, { objectType: 'Asset' })
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.jobTaskSettings?.assetId,
      this.props.linkTargetRecord?.assetId,
      this.props.linkTargetRecord?.objectId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const { description = '', manufacturer = '', replacementBarcode = '' } = this.props
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                assetCategoryId: 0,
                supervisor: '',
                overdueNotificationGroupId: 0,
                price: 0,
                mileage: 0,
                serviceHours: 0,
                description,
                locationId: this.props.locationId,
                manufacturer,
                replacementBarcode,
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getLocations(),
        this.props.getAssetCategories({ includeUnassignedSelection: false }),
        this.props.getUsers({ active: 'Active', vending: 'NonVending' }),
        this.props.getUserGroups(),
      ])

      const item = cloneDeep(responses[0].value.data)

      const [documentItems, warrantyItems, toleranceItems, historyItems, image, taskItems, formInstanceItems] =
        await Promise.all([
          item.id
            ? this.props.getDocumentItems({
                request: {
                  domainObjectId: item.id,
                  domainObjectType: 'Asset',
                  documentType: 'ObjectDocument',
                },
              })
            : Promise.resolve(null),
          item.id ? this.props.getWarrantyItems(item.id) : Promise.resolve(null),
          item.id ? this.props.getToleranceItems(item.id) : Promise.resolve(null),
          item.id ? this.props.getHistoryItems(item.id) : Promise.resolve(null),
          item.id
            ? this.props
                .getDocumentContents({
                  request: {
                    domainObjectId: item.id,
                    domainObjectType: 'Asset',
                    documentType: 'ObjectImage',
                    documentName: item.barcode,
                  },
                })
                .catch(() => {})
            : Promise.resolve(null),
          item.id
            ? this.props.getTaskItems({
                active: 'All',
                taskType: taskTypes.ScheduledJob,
                assetId: item.id,
              })
            : Promise.resolve(null),
          item.id ? this.props.getFormInstanceItems({ objectType: 'Asset', objectId: item.id }) : Promise.resolve(null),
        ])

      this.setState(
        {
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          locations: get(responses[2], 'value.data.items', []),
          assetCategories: get(responses[3], 'value.data.items', []),
          users: get(responses[4], 'value.data.items', []),
          userGroups: get(responses[5], 'value.data.items', []),
          documentItems: get(documentItems, 'value.data.items', []),
          documentItemsOriginal: get(documentItems, 'value.data.items', []),
          documentItemsTotals: get(documentItems, 'value.data.pageTotals', {}),
          warrantyItems: get(warrantyItems, 'value.data.items', []),
          warrantyItemsOriginal: get(warrantyItems, 'value.data.items', []),
          warrantyItemsTotals: get(warrantyItems, 'value.data.pageTotals', {}),
          toleranceItems: get(toleranceItems, 'value.data.items', []),
          toleranceItemsOriginal: get(toleranceItems, 'value.data.items', []),
          toleranceItemsTotals: get(toleranceItems, 'value.data.pageTotals', {}),
          historyItems: get(historyItems, 'value.data.items', []),
          historyItemsOriginal: get(historyItems, 'value.data.items', []),
          historyItemsTotals: get(historyItems, 'value.data.pageTotals', {}),
          image: image?.value?.data,
          imageOriginal: image?.value?.data,
          taskItems: get(taskItems, 'value.data.items', []),
          taskItemsOriginal: get(taskItems, 'value.data.items', []),
          taskItemsTotals: get(taskItems, 'value.data.pageTotals', {}),
          formInstanceItems: get(formInstanceItems, 'value.data.items', []),
          formInstanceItemsOriginal: get(formInstanceItems, 'value.data.items', []),
          formInstanceItemsTotals: get(formInstanceItems, 'value.data.pageTotals', {}),
          formHasChanged: false,
        },
        this.fetchInventoryAvailability
      )
    } catch (error) {
      showError({ error })
    }
  }

  saveTaskItems = async (assetId) => {
    const { creating, updating, deleting } = getChangedItems(this.state.taskItemsOriginal, this.state.taskItems)

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteTaskItems(deleting.map((each) => each.id))

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateTaskItems(updating)

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createTaskItems(
        creating.map(
          produce((draft) => {
            unset(draft, 'id')
            unset(draft, 'taskId')
            set(draft, 'lastRun', '0001-01-01T12:00:00Z')
            set(draft, 'jobTaskSettings.assetBarcode', this.state.item.barcode)
            set(draft, 'jobTaskSettings.assetId', assetId)
            set(draft, 'jobTaskSettings.previousJobId', 0)
          })
        )
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([
      this.saveDocumentItems(saved.value.data.id),
      this.saveWarrantyItems(saved.value.data.id),
      this.saveToleranceItems(saved.value.data.id),
      this.saveTaskItems(saved.value.data.id),
      this.saveImage(saved.value.data.id, item.barcode),
    ])

    return saved
  }

  handleFileUpload = (fileList = []) => {
    if (fileList[0]) {
      this.setState({
        image: {
          contents: fileList[0].fileContents,
          fileType: fileList[0].fileType,
        },
        fileUploadDrawerVisible: false,
      })
    } else {
      this.setState({ fileUploadDrawerVisible: false })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      () => {
        if (['locationId', 'replacementBarcode'].includes(name)) {
          this.fetchInventoryAvailability()
        }
      }
    )

  fetchInventoryAvailability = async () => {
    const { locationId, replacementBarcode: barcode } = this.state.item ?? {}

    if (locationId && barcode) {
      try {
        const response = await this.props.getInventoryAvailability({
          locationId,
          barcode,
        })

        this.setState({ inventoryAvailability: response.value.data })
      } catch (error) {
        showError({ error })
      }
    }
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'jobs':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/jobs/jobs?${encodeState({
              showFilter: true,
              filterDto: { assetIds: [this.state.item.id] },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'jobCostReport':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/jobs/reports/jobCostReport?${encodeState({
              showFilter: true,
              filterDto: { assetIds: [this.state.item.id] },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'trackAsset':
        confirmUnsavedChanges(() => {
          setSessionItem(TRACK_ASSETS_AFTER_CLOSE_KEY, {
            redirectUrl: window.location.pathname,
            linkTarget: {
              linkTargetName: 'assetRecord',
              linkTargetRecord: { assetId: this.state.item.id },
              linkTargetText: this.state.item.barcode,
            },
          })
          setStorageItem('assets.trackAssets', {
            quickscan: true,
            assetId: this.state.item.id,
            operatorId: this.props.user.operatorId,
            operatorBarcode: this.props.user.operatorBarcode,
            operatorDisplayName: this.props.user.operatorName,
          })
          this.props.history.push('/assets/trackAssets')
          this.props.onCancel()
        })
        break

      case 'createJob':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ createJobVisible: true, createJobSaving: false })
        }
        break

      case 'processSetupSheet':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ processSetupSheetVisible: true })
        }
        break

      case 'copyFromExistingRecord':
        this.setState({
          copyFromExistingVisible: true,
          copyFromExistingSelectedRecord: null,
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromExistingLoading: true })

      const { id, ...rest } = await this.props
        .getItem(this.state.copyFromExistingSelectedRecord.id)
        .then((r) => r.value.data)
        .then(cloneDeep)

      const [warrantyItems, toleranceItems] = await Promise.all([
        this.props.getWarrantyItems(id),
        this.props.getToleranceItems(id),
      ])

      let seed = Date.now() * -1

      this.setState(
        produce((draft) => {
          Object.assign(draft.item, rest)

          draft.warrantyItems = warrantyItems.value.data.items.map((each) => ({
            ...each,
            id: seed--,
            assetId: id,
          }))
          draft.toleranceItems = toleranceItems.value.data.items.map((each) => ({
            ...each,
            id: seed--,
            assetId: id,
          }))

          draft.copyFromExistingVisible = false
          draft.copyFromExistingSelectedRecord = null
        })
      )
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromExistingLoading: false })
    }
  }

  updateField = (dtoFieldName) => {
    switch (dtoFieldName) {
      case 'mileage':
      case 'serviceHours':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t(camelCase(`confirmUpdate-${dtoFieldName}`))
            .replace('xxx', this.state.itemOriginal[dtoFieldName])
            .replace('yyy', this.state.item[dtoFieldName]),
          okType: 'primary',
          okText: t('update'),
          cancelText: t('cancel'),
          onOk: async () => {
            try {
              await this.props.onSave?.(true)

              const response = await invoke(this.props, camelCase(`update-${dtoFieldName}`), this.state.item)

              showClientNotifications({ response })

              const historyItems = await this.props.getHistoryItems(this.state.item.id).then((r) => r.value.data.items)

              this.setState(
                produce((draft) => {
                  draft.itemOriginal[dtoFieldName] = draft.item[dtoFieldName]
                  draft.historyItems = draft.historyItemsOriginal = historyItems
                })
              )
            } catch (error) {
              showError({ error })
            } finally {
              await this.props.onSave?.(false)
            }
          },
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleGenerateClick = async (e) => {
    let saved = null

    try {
      stopEvent(e)

      const params = {
        ...this.props.form.getFieldsValue(),
        createdDate: new Date().toJSON(),
        createdBy: this.props.user.userName,
        id: undefined,
      }

      if (!params.locationId) {
        message.error(t('selectLocationFirst'))
        return
      }

      this.setState({ validationErrors: null, generateButtonLoading: true })

      await this.props.onSave?.(true)

      saved = await this.props.generateId(params).then((r) => r.value.data)

      await this.fetchItem({ itemId: saved.id })

      await this.props.onSave?.(false, saved)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false, saved)
    } finally {
      this.setState({ generateButtonLoading: false })
    }
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.documentItems, this.state.documentItemsOriginal) ||
    !isEqual(this.state.warrantyItems, this.state.warrantyItemsOriginal) ||
    !isEqual(this.state.toleranceItems, this.state.toleranceItemsOriginal) ||
    !isEqual(this.state.historyItems, this.state.historyItemsOriginal) ||
    !isEqual(this.state.taskItems, this.state.taskItemsOriginal)

  render() {
    const { item, fieldSettings, inventoryAvailability } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || getUserPermission('Assets') !== 'Edit'

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick}>
                <Menu.Item key="createJob" disabled={!item.id}>
                  {t('createJob')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="jobs" disabled={!item.id}>
                  {t('jobs')}
                </Menu.Item>
                {getUserPermission('Reports') !== 'No' && (
                  <Menu.Item key="jobCostReport" disabled={!item.id}>
                    {t('jobCostReport')}
                  </Menu.Item>
                )}
                <Menu.Item key="processSetupSheet" disabled={!item.id}>
                  {t('processSetupSheet')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="trackAsset" disabled={!item.id}>
                  {t('trackAsset')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="copyFromExistingRecord" disabled={readOnly || Boolean(item.id)}>
                  {t('copyFromExistingAsset')}
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col third>
                <Form.Item label={createLabel('locationId')}>
                  {createFieldDecorator('locationId')(
                    <Select
                      allowClear={false}
                      placeholder={t('none')}
                      onChange={(value) => this.setItemValue('locationId', value)}
                      readOnly={readOnly}
                    >
                      {(this.state.locations ?? []).map((each) => (
                        <Option key={each.id} value={each.id}>
                          <span {...getOptionProps(each)}>{each.displayName}</span>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('assetCategoryId')}>
                  {createFieldDecorator('assetCategoryId')(
                    <Select
                      onChange={(value) => this.setItemValue('assetCategoryId', value || 0)}
                      placeholder={t('none')}
                      readOnly={readOnly}
                    >
                      <Option value={0}>{t('none')}</Option>
                      {(this.state.assetCategories ?? [])
                        .filter((each) => each.id)
                        .map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('serviceStatus')}>
                  {createFieldDecorator('serviceStatus')(
                    <Select placeholder={t('none')} readOnly={readOnly} disabled={item.status === 'Out'}>
                      {Object.entries(serviceStatusOptions).map(([key, value]) => (
                        <Option key={key} value={key}>
                          {t(value)}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col third>
                <Form.Item label={createLabel('barcode')}>
                  <Row gutter={8} type="flex">
                    <Col xs={!item.id ? 16 : 24}>
                      {createFieldDecorator('barcode', { extraRules: [validateBarcode()] })(
                        <Input readOnly={readOnly} />
                      )}
                    </Col>
                    {!item.id && (
                      <Col xs={8}>
                        <Button
                          className="w-full"
                          onClick={this.handleGenerateClick}
                          loading={this.state.generateButtonLoading}
                        >
                          {t('generate')}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('itemNumber')}>
                  {createFieldDecorator('itemNumber')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('inServiceDate')}>
                  {createFieldDecorator('inServiceDate')(<DatePicker readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col twoThirds>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('revisionDate')}>
                  {createFieldDecorator('revisionDate')(<DatePicker readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey || 'general'}
              onChange={(value) => this.setState({ tabsActiveKey: value })}
            >
              <Tabs.TabPane key="general" tab={t('general')} forceRender>
                <Row>
                  <Col third>
                    <Form.Item label={createLabel('binLocation')}>
                      {createFieldDecorator('binLocation')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('manufacturer')}>
                      {createFieldDecorator('manufacturer')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('modelNumber')}>
                      {createFieldDecorator('modelNumber')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('serialNumber')}>
                      {createFieldDecorator('serialNumber')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('supervisor')}>
                      {createFieldDecorator('supervisor')(
                        <Select placeholder={t('none')} readOnly={readOnly} showSearch>
                          <Option value="">{t('none')}</Option>
                          {(this.state.users ?? []).map((each) => (
                            <Option key={each.userName} value={each.userName}>
                              <span {...getOptionProps(each)}>{each.displayName}</span>
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col third>
                    <Form.Item label={createLabel('accountNumber')}>
                      {createFieldDecorator('accountNumber')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('costCenter')}>
                      {createFieldDecorator('costCenter')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    {allowPricing() && (
                      <Form.Item label={createLabel('price')}>
                        {createFieldDecorator('price')(<InputNumber readOnly={readOnly} min={0} currency />)}
                      </Form.Item>
                    )}
                    <Form.Item label={createLabel('maxCheckoutDays')}>
                      {createFieldDecorator('maxCheckoutDays')(<InputNumber min={0} readOnly={readOnly} />)}
                    </Form.Item>
                    <Form.Item label={createLabel('overdueNotificationGroupId')}>
                      {createFieldDecorator('overdueNotificationGroupId')(
                        <Select
                          placeholder={t('none')}
                          onChange={(value) => this.setItemValue('overdueNotificationGroupId', value || 0)}
                          readOnly={readOnly}
                          showSearch
                        >
                          <Option value={0}>{t('none')}</Option>
                          {(this.state.userGroups ?? [])
                            .filter((each) => each.id)
                            .map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.description}</span>
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col third>
                    <InputImage
                      label={t('assetImage')}
                      onUpload={() => this.setState({ fileUploadDrawerVisible: true })}
                      onDelete={() => this.setState({ image: null })}
                      value={this.state.image}
                      readOnly={readOnly}
                    />
                    <Form.Item label={createLabel('replacementBarcode')}>
                      <div className="tofino-label-buttons">
                        <Button size="small" disabled>
                          <Icon type="ShoppingCart" />
                        </Button>
                      </div>
                      {createFieldDecorator('replacementBarcode', {
                        extraRules: [validateBarcode()],
                      })(
                        <Input
                          onChange={(e) => this.setItemValue('replacementBarcode', e.target.value)}
                          readOnly={readOnly}
                        />
                      )}
                    </Form.Item>
                    <Form.Item label={t('inventoryAvailability')}>
                      <div className="tofino-label-buttons">
                        <Button
                          size="small"
                          onClick={() =>
                            this.setState({
                              editInventoryVisible: true,
                              editInventorySaving: false,
                            })
                          }
                          disabled={!inventoryAvailability?.id || readOnly}
                        >
                          <Icon type="Edit" />
                        </Button>
                      </div>
                      <Input
                        value={
                          isNil(item.locationId)
                            ? t('na')
                            : isEmpty(item.replacementBarcode)
                              ? t('noReorderBarcodeDefined')
                              : inventoryAvailability?.id
                                ? Object.entries({
                                    onHand: 'onhand',
                                    onHand2: 'onhand2',
                                    onOrder: 'onOrder',
                                  })
                                    .map(([key, value]) => `${t(value)}: ${get(inventoryAvailability, key, '?')}`)
                                    .join(', ')
                                : t('na')
                        }
                        disabled={!inventoryAvailability?.id}
                        readOnly
                      />
                    </Form.Item>
                    <Row>
                      <Col xs={12}>
                        {createFieldDecorator('isCritical', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('isCritical')}</Checkbox>
                        )}
                      </Col>
                      <Col xs={12}>
                        {createFieldDecorator('isRental', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('isRental')}</Checkbox>
                        )}
                      </Col>
                    </Row>
                    <Form.Item label={HARD_SPACE} className="mb-0">
                      {createFieldDecorator('certificationRequired', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('certificationRequired')}</Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col half>
                    <Form.Item label={createLabel('keywords')}>
                      {createFieldDecorator('keywords')(<Input.TextArea rows={5} readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col half>
                    <Form.Item label={createLabel('comment')}>
                      {createFieldDecorator('comment')(<Input.TextArea rows={5} readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="maintenance" tab={t('maintenance')} forceRender>
                <Row>
                  {['lastCalibrationDate', 'nextCalibrationDate'].map((each) => (
                    <Col key={each} sm={6} xs={24}>
                      <Form.Item label={createLabel(each)}>
                        {createFieldDecorator(each)(<DatePicker readOnly={readOnly} />)}
                      </Form.Item>
                    </Col>
                  ))}
                  {['serviceHours', 'mileage'].map((each) => (
                    <Col key={each} sm={6} xs={24}>
                      <Form.Item label={createLabel(each)}>
                        {this.state.item.id ? (
                          <div className="tofino-label-buttons">
                            <Button
                              size="small"
                              disabled={this.state.item[each] === this.state.itemOriginal[each]}
                              onClick={() => this.updateField(each)}
                            >
                              <Icon type="Save" />
                            </Button>
                          </div>
                        ) : null}
                        {createFieldDecorator(each)(
                          <InputNumber onChange={(value) => this.setItemValue(each, tryParseFloat(value, 0))} min={0} />
                        )}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
                <Divider orientation="left" className="mt-6">
                  {t('scheduledJobs')}
                </Divider>
                <Tasks
                  taskType="ScheduledJob"
                  parentRecord={item}
                  items={this.state.taskItems ?? []}
                  pageTotals={this.state.taskItemsTotals ?? {}}
                  onChange={(values) => this.setState({ taskItems: values })}
                  settingsType="asset"
                  readOnly={readOnly}
                  onTableExtraButtonsClick={({ key }) => {
                    if (key === 'copySchedule') {
                      this.setState({ copyScheduleVisible: true })
                    }
                  }}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="tolerances" tab={t('tolerances')} forceRender>
                <Tolerances
                  settingsType="assets"
                  items={this.state.toleranceItems ?? []}
                  pageTotals={this.state.toleranceItemsTotals ?? {}}
                  onChange={(values) => this.setState({ toleranceItems: values })}
                  readOnly={readOnly}
                  parentRecord={item}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="warranty" tab={t('warranty')} forceRender>
                <Warranties
                  items={this.state.warrantyItems ?? []}
                  pageTotals={this.state.warrantyItemsTotals ?? {}}
                  onChange={(values) => this.setState({ warrantyItems: values })}
                  readOnly={readOnly}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="loto" tab={t('loto')} forceRender>
                {createFieldDecorator('lotoNotes')(<Input.TextArea rows={14} className="w-full" readOnly={readOnly} />)}
              </Tabs.TabPane>
              <Tabs.TabPane key="history" tab={t('history')} forceRender>
                <History
                  items={this.state.historyItems ?? []}
                  pageTotals={this.state.historyItemsTotals ?? {}}
                  onChange={(values) => this.setState({ historyItems: values })}
                  readOnly={readOnly}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                <Documents
                  domainObjectId={item.id}
                  domainObjectType="Asset"
                  items={this.state.documentItems ?? []}
                  pageTotals={this.state.documentItemsTotals ?? {}}
                  onChange={(values) => this.setState({ documentItems: values })}
                  readOnly={readOnly}
                />
              </Tabs.TabPane>
              {!isNil(item.id) && (
                <Tabs.TabPane key="lists" tab={t('lists')} forceRender>
                  <Lists
                    entityType="Asset"
                    entityId={item.id}
                    domainObjectType="Asset"
                    locationId={item.locationId}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              {!isNil(item.id) && (getUserPermission('Master') === 'Yes' || getUserPermission('Forms') !== 'None') && (
                <Tabs.TabPane key="forms" tab={t('forms')} forceRender>
                  <FormInstancesContext.Provider value={{}}>
                    <FormInstances
                      settingsType="asset"
                      objectType="Asset"
                      objectId={item.id}
                      parentRecord={item}
                      items={this.state.formInstanceItems ?? []}
                      pageTotals={this.state.formInstanceItemsTotals ?? {}}
                      onChange={this.saveFormInstances}
                      readOnly={readOnly}
                    />
                  </FormInstancesContext.Provider>
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Drawer
          title={inventoryAvailability?.barcode}
          size={linkTargets.inventoryRecord.formSize}
          visible={this.state.editInventoryVisible}
          onClose={() => this.setState({ editInventoryVisible: false })}
          saving={this.state.editInventorySaving}
        >
          <InventoryFormView
            linkTargetRecord={{ inventoryId: inventoryAvailability?.id }}
            onSave={(pending) =>
              this.promiseState({ editInventorySaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchInventoryAvailability()
                }
              })
            }
            onSaveAndClose={() => this.setState({ editInventoryVisible: false })}
            onCancel={() => this.setState({ editInventoryVisible: false })}
          />
        </Drawer>
        <Drawer
          title={t('selectFile')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
          />
        </Drawer>
        <Drawer
          title={t('createJob')}
          size={linkTargets.jobRecord.formSize}
          visible={this.state.createJobVisible}
          onClose={() => this.setState({ createJobVisible: false })}
          saving={this.state.createJobSaving}
        >
          <JobsFormView
            assetId={item.id}
            locationId={item.locationId}
            assetCategoryId={item.assetCategoryId}
            onSave={(pending) =>
              this.promiseState({ createJobSaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ createJobVisible: false })}
            onCancel={() => this.setState({ createJobVisible: false })}
          />
        </Drawer>
        {this.state.copyScheduleVisible && (
          <CopySchedule
            onCopy={(copiedTaskItems) =>
              this.setState(
                produce((draft) => {
                  const seed = Date.now() * -1

                  draft.taskItems.push(
                    ...copiedTaskItems.map((each, index) => ({
                      ...each,
                      id: seed - index,
                      taskId: seed - index,
                    }))
                  )

                  draft.copyScheduleVisible = false
                })
              )
            }
            onCancel={() => this.setState({ copyScheduleVisible: false })}
          />
        )}
        <Modal
          title={t('selectAsset')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedRecord,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedRecord: null,
            })
          }
          width={992}
        >
          <SelectAsset onSelect={(values) => this.setState({ copyFromExistingSelectedRecord: values[0] })} />
        </Modal>
        <Drawer
          title={t('processSetupSheet')}
          size="lg"
          visible={this.state.processSetupSheetVisible}
          onClose={() => this.setState({ processSetupSheetVisible: false })}
        >
          <ProcessSetupSheet
            assetId={this.state.item.id}
            onCancel={() => this.setState({ processSetupSheetVisible: false })}
          />
        </Drawer>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
